import React, {
  FC,
  useMemo,
  useCallback,
  useEffect,
  useState,
  ChangeEvent,
} from "react";
import { Grid, Loader, Icon, TextArea, Popup } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../selectors/state";
import { closeSlidingMenuLayer } from "../../actions/toggleSlidingMenu";
import FancyHeader from "../../components/shared/FancyHeader";
import useForm from "../../custom-hooks/form/useForm";
import useFormWarning from "../../custom-hooks/form/useFormWarning";
import FetchAPI from "../../api/FetchAPI";
import { handleScrollToItem, toastError } from "../../app_shared_functions";
import FormInputField from "../marketplace-app-form/form-fields/FormInputField";
import SelectField from "../marketplace-app-form/form-fields/SelectField";
import { ContactFormData } from "./types";
import { toast } from "react-toastify";

const MarketplaceContactSlidingPanel: FC = () => {
  const dispatch = useDispatch();
  const selectedApp = useSelector(
    (state: State) => state.marketplace.selectedApp,
  );
  const user = useSelector((state: State) => state.login?.userlogin);
  const [countriesList, setCountriesList] = useState<string[] | "error" | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialState: ContactFormData = useMemo(
    () => ({
      name:
        user && user.firstname && user.lastname
          ? user.firstname + " " + user.lastname
          : "",
      email: user?.email || "",
      country: "",
      phone: user?.phone || "",
      additionalInfo: "",
    }),
    [user],
  );

  const validations = useMemo(
    () => [
      {
        field: "name",
        requiredMessage: "Please provide a name for your Contact",
        ref: null,
      },
      {
        field: "email",
        requiredMessage: "Please provide a valid Email address",
        ref: null,
        regexPattern: {
          value: "\\S+@\\S+\\.\\S+",
          errorMessage: "Please provide a valid Email address",
        },
      },
      {
        field: "country",
        requiredMessage: "Please provide a valid Country",
        ref: null,
      },
      {
        field: "phone",
        ref: null,
        regexPattern: {
          value: "^\\+[0-9]{8,15}$",
          errorMessage:
            "Please provide a valid phone number with country code (e.g., +46123456789)",
        },
      },
    ],
    [],
  );

  const { formData, error, handleChange } = useForm({
    validations,
    initialState,
  });
  const { formWarning, showFormWarning } = useFormWarning();

  const onFieldChange = useCallback(
    (key: string) =>
      (e: ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        handleChange({ name: key, value: data.value });
      },
    [handleChange],
  );

  const handleAdditionalInfoChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>): void => {
      handleChange({ name: "additionalInfo", value: e.target.value });
    },
    [handleChange],
  );

  useEffect(() => {
    if (!selectedApp) return;
    FetchAPI.Base.getCountriesList()
      .then((response) => {
        const countries = response.data
          .sort((a, b) => (a.name === "SWEDEN" ? -1 : 1))
          .map((c) => c.name);
        setCountriesList(countries);
        if (countries.includes("SWEDEN")) {
          handleChange({ name: "country", value: "SWEDEN" });
        }
      })
      .catch(() => {
        setCountriesList("error");
      });
  }, [selectedApp, handleChange]);

  const handleSubmit = useCallback(async () => {
    if (error) {
      showFormWarning();
      if (error.ref && error.ref.current) {
        handleScrollToItem(error.ref.current, true);
      }
      return;
    }

    if (!selectedApp) {
      console.error("No selected app. Aborting submission.");
      return;
    }

    setIsSubmitting(true);
    const objectToSend = {
      name: formData.name,
      email: formData.email,
      country: formData.country,
      phone: formData.phone,
      additionalInfo: formData.additionalInfo,
    };
    try {
      await FetchAPI.Marketplace.contactUs(selectedApp.id, objectToSend);
      toast.success(
        "Your message has been sent successfully. We will get back to you soon.",
      );
      dispatch(closeSlidingMenuLayer());
    } catch (err) {
      toastError(error, "Failed to load invoice settings");
      dispatch(closeSlidingMenuLayer());
    } finally {
      setIsSubmitting(false);
    }
  }, [error, formData, dispatch, showFormWarning, selectedApp]);

  if (!selectedApp) {
    return <div>Loading...</div>;
  }

  return (
    <div className="marketplace-contact-form">
      <FancyHeader title="Contact Cleura" />
      <p></p>
      <FormInputField
        inputKey="name"
        title="Name"
        value={formData.name}
        isRequired={true}
        errorMessage={error?.field === "name" ? error.message : ""}
        inputType="text"
        onChange={onFieldChange}
        disabled={false}
        pattern=""
        description=""
      />
      <FormInputField
        inputKey="email"
        title="Email"
        value={formData.email}
        isRequired={true}
        errorMessage={error?.field === "email" ? error.message : ""}
        inputType="text"
        onChange={onFieldChange}
        disabled={false}
        pattern=""
        description=""
      />
      {countriesList ? (
        Array.isArray(countriesList) ? (
          <Grid>
            <SelectField
              title="Country"
              inputKey="country"
              value={formData.country}
              isRequired={true}
              handleChange={handleChange}
              options={countriesList}
              errorMessage={error?.field === "country" ? error.message : ""}
            />
          </Grid>
        ) : (
          <FormInputField
            inputKey="country"
            title="Country"
            value="Countries list not loaded"
            isRequired={true}
            errorMessage=""
            inputType="text"
            onChange={onFieldChange}
            disabled={true}
            pattern=""
            description=""
          />
        )
      ) : (
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <Loader active inline="centered" size="mini">
                Countries loading...
              </Loader>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      <FormInputField
        inputKey="phone"
        title="Phone Number"
        value={formData.phone}
        isRequired={false}
        errorMessage={error?.field === "phone" ? error.message : ""}
        inputType="text"
        onChange={onFieldChange}
        disabled={false}
        pattern="^\\+[0-9]{8,15}$"
        description=""
      />
      <Grid>
        <Grid.Row>
          <Grid.Column textAlign="left" width={8}>
            <h5>Additional Information</h5>
          </Grid.Column>
          <Grid.Column textAlign="left" width={8}>
            <TextArea
              name="additionalInfo"
              placeholder="Enter any additional details here..."
              value={formData.additionalInfo}
              onChange={handleAdditionalInfoChange}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column textAlign="left" width={16}>
            <a
              href="https://cleura.com/resources/legal/privacy-notice/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Cleura&apos;s Privacy Policy
            </a>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="padding-top-30">
          <Grid.Column width={16}>
            {error ? (
              <Popup
                trigger={
                  <button
                    className="float-right button button--green button--disabled button--icon__left"
                    onClick={() => {
                      showFormWarning();
                      if (error.ref && error.ref.current) {
                        handleScrollToItem(error.ref.current, true);
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <Icon name="exclamation circle" />
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                }
                content={error.message}
                position="top right"
              />
            ) : (
              <button
                className="float-right button button--green"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            )}
            <button
              className="button button--bordered"
              onClick={() => dispatch(closeSlidingMenuLayer())}
              disabled={isSubmitting}
            >
              Back
            </button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default MarketplaceContactSlidingPanel;
